// src/components/AdminCircular.jsx
// Mobile View Optimized - Blue Theme (No Borders)
// Colors: #0D47A1, #2196F3, #90CAF9, #E3F2FD

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { User, XCircle, CheckCircle2, X } from "lucide-react";
const CLASS_OPTIONS = ["LKG", "UKG", "Class 1", "Class 2", "Class 3", "Class 4", "Class 5", "Class 6", "Class 7", "Class 8", "Class 9", "Class 10", "Class 11", "Class 12"];
const SECTIONS = ["A", "B", "C"];
function StatusBadge({
  status
}) {
  const map = {
    sent: {
      bg: "#0D47A1",
      color: "#FFFFFF"
    },
    draft: {
      bg: "#E3F2FD",
      color: "#0D47A1"
    },
    pending: {
      bg: "#E3F2FD",
      color: "#0D47A1"
    },
    principal: {
      bg: "#2196F3",
      color: "#FFFFFF"
    }
  };
  const labels = {
    sent: "Sent",
    draft: "Draft",
    pending: "Pending",
    principal: "Principal"
  };
  const cfg = map[status] || map.draft;
  return <span style={{
    background: cfg.bg,
    color: cfg.color,
    padding: "4px 12px",
    borderRadius: 24,
    fontSize: 11,
    fontWeight: 600,
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
  }}>
      <span style={{
      width: 6,
      height: 6,
      borderRadius: "50%",
      background: cfg.color,
      display: "inline-block"
    }} />
      {labels[status] || status}
    </span>;
}
export default function AdminCircular() {
  const navigate = useNavigate();
  const API_BASE = process.env.REACT_APP_API_URL || 'https://schoolcrm-fzcv.onrender.com';
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [allParents, setAllParents] = useState(false);
  const [allStaff, setAllStaff] = useState(false);
  const [toPrincipal, setToPrincipal] = useState(false);
  const [classesSel, setClassesSel] = useState([]);
  const [sending, setSending] = useState(false);
  const [recent, setRecent] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [notice, setNotice] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Modal states
  const [showRecipientModal, setShowRecipientModal] = useState(false);
  const [selectedClassForModal, setSelectedClassForModal] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  useEffect(() => {
    fetchRecent();
  }, []);

  // Clear other selections when Principal is selected
  useEffect(() => {
    if (toPrincipal) {
      setAllParents(false);
      setAllStaff(false);
      setClassesSel([]);
    }
  }, [toPrincipal]);
  async function fetchRecent() {
    try {
      const res = await fetch(`${API_BASE}/api/circulars`);
      const j = await res.json();
      if (j.success) setRecent(j.circulars);
    } catch (err) {
      console.error(err);
    }
  }

  // Handle class selection - shows modal
  function handleClassSelect(cls) {
    setSelectedClassForModal(cls);
    setSelectedType(null);
    setSelectedSections([]);
    setShowRecipientModal(true);
  }

  // Handle send from modal
  async function handleModalSend() {
    if (!selectedType || selectedSections.length === 0) {
      alert('Please select recipient type and at least one section');
      return;
    }
    setModalLoading(true);
    try {
      const className = selectedClassForModal;
      const type = selectedType;
      const sections = selectedSections;
      const recipientsMeta = {
        allStaff: type === 'staff',
        allParents: type === 'parents',
        toPrincipal: false,
        classes: [className]
      };
      const body = {
        title,
        message,
        recipientsMeta,
        isDraft: false,
        sections: sections,
        recipientType: type
      };
      let res, j;
      if (editingId) {
        res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      } else {
        res = await fetch(`${API_BASE}/api/circulars`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      }
      if (j.success) {
        setNotice(`Circular sent to ${type} in ${className} (Sections: ${sections.join(', ')})`);
        setTitle('');
        setMessage('');
        setClassesSel([]);
        setAllParents(false);
        setAllStaff(false);
        setToPrincipal(false);
        setEditingId(null);
        setShowRecipientModal(false);
        fetchRecent();
      } else {
        setNotice("Send failed:" + (j.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      setNotice("Send failed:" + err.message);
    }
    setModalLoading(false);
  }

  // Handle Send Now button click
  async function handleSendNow() {
    if (toPrincipal) {
      setSending(true);
      setNotice('');
      try {
        let principalName = 'Principal';
        let principalId = null;
        try {
          const principalRes = await fetch(`${API_BASE}/api/headmistress`);
          const principalData = await principalRes.json();
          if (principalData.success && principalData.data) {
            principalName = principalData.data.name || 'Principal';
            principalId = principalData.data._id || null;
          }
        } catch (err) {
          console.log('Could not fetch principal details:', err);
        }
        const body = {
          title,
          message,
          recipientsMeta: {
            allParents: false,
            allStaff: false,
            toPrincipal: true,
            classes: []
          },
          isDraft: false,
          recipientType: 'principal',
          sections: [],
          principalName: principalName,
          principalId: principalId
        };
        let res, j;
        if (editingId) {
          res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          j = await res.json();
        } else {
          res = await fetch(`${API_BASE}/api/circulars`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(body)
          });
          j = await res.json();
        }
        if (j.success) {
          setNotice(`Circular sent to ${principalName}`);
          setTitle('');
          setMessage('');
          setClassesSel([]);
          setAllParents(false);
          setAllStaff(false);
          setToPrincipal(false);
          setEditingId(null);
          fetchRecent();
        } else {
          setNotice("Send failed:" + (j.error || 'Unknown error'));
        }
      } catch (err) {
        console.error(err);
        setNotice("Send failed:" + err.message);
      }
      setSending(false);
      return;
    }
    if (classesSel.length === 1) {
      handleClassSelect(classesSel[0]);
      return;
    }

    // ✅ FIX: Previously, whenever no specific class was picked
    // (classesSel.length === 0), BOTH allParents and allStaff were forced to
    // `true` no matter what the admin actually checked — so ticking only
    // "All Parents" would silently also send the circular to every staff
    // member (and vice versa). Now we send exactly what was selected, and
    // block the send with a clear message if nothing was actually chosen.
    if (!allParents && !allStaff && classesSel.length === 0) {
      setNotice("Please select at least one recipient (All Parents, All Staff, Principal, or a Class) before sending.");
      return;
    }
    setSending(true);
    setNotice('');
    try {
      const body = {
        title,
        message,
        recipientsMeta: {
          allParents: allParents,
          allStaff: allStaff,
          toPrincipal: false,
          classes: classesSel
        },
        isDraft: false,
        sections: [],
        recipientType: ''
      };
      let res, j;
      if (editingId) {
        res = await fetch(`${API_BASE}/api/circulars/${editingId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      } else {
        res = await fetch(`${API_BASE}/api/circulars`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(body)
        });
        j = await res.json();
      }
      if (j.success) {
        setNotice(editingId ? "Circular updated & sent" : "Circular sent");
        setTitle('');
        setMessage('');
        setClassesSel([]);
        setAllParents(false);
        setAllStaff(false);
        setToPrincipal(false);
        setEditingId(null);
        fetchRecent();
      } else {
        setNotice("Send failed:" + (j.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      setNotice("Send failed:" + err.message);
    }
    setSending(false);
  }
  function startEditCircular(id) {
    const c = recent.find(x => x._id === id);
    if (!c) return;
    setTitle(c.title || '');
    setMessage(c.message || '');
    setAllParents(!!c.recipientsMeta?.allParents);
    setAllStaff(!!c.recipientsMeta?.allStaff);
    setToPrincipal(!!c.recipientsMeta?.toPrincipal);
    setClassesSel(Array.isArray(c.recipientsMeta?.classes) ? c.recipientsMeta.classes : []);
    setEditingId(id);
    setNotice(" Editing circular");
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }
  async function deleteCircular(id) {
    if (!window.confirm('Delete this circular?')) return;
    try {
      const res = await fetch(`${API_BASE}/api/circulars/${id}`, {
        method: 'DELETE'
      });
      const j = await res.json();
      if (j.success) {
        setNotice(" Deleted");
        fetchRecent();
      } else setNotice("Delete failed");
    } catch (err) {
      console.error(err);
      setNotice("Delete failed");
    }
  }
  function toggleClass(cls) {
    setClassesSel(prev => prev.includes(cls) ? prev.filter(x => x !== cls) : [...prev, cls]);
  }
  function toggleSection(section) {
    setSelectedSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
  }
  const getCircularStatus = r => {
    if (r.recipientsMeta?.toPrincipal) return 'principal';
    return r.isDraft ? 'draft' : 'sent';
  };
  // Distinct blue-theme accent color per recipient type, so Principal /
  // Staff / Parents circulars are visually distinguishable at a glance in
  // the Recent Circulars list — using only the approved blue palette.
  const getRecipientAccentColor = r => {
    if (r.recipientsMeta?.toPrincipal) return "#2196F3"; // Principal
    const type = (r.recipientType || '').toLowerCase();
    if (type === 'staff' || r.recipientsMeta?.allStaff) return "#0D47A1"; // Staff
    if (type === 'parents' || r.recipientsMeta?.allParents) return "#90CAF9"; // Parents
    return "#0D47A1"; // fallback (e.g. mixed / general class circular)
  };
  const getRecipientText = r => {
    if (r.recipientsMeta?.toPrincipal) {
      const principalRecipient = r.recipients?.find(rec => rec.type === 'principal');
      if (principalRecipient?.name) {
        return `${principalRecipient.name}`;
      }
      return "Principal Only";
    }
    let text = "";
    if (r.recipientsMeta?.allParents) text += "All Parents ";
    if (r.recipientsMeta?.allStaff) text += "All Staff ";
    if (r.recipientsMeta?.classes?.length) {
      text += `Classes: ${r.recipientsMeta.classes.join(', ')}`;
    }
    if (r.sections && r.sections.length > 0) {
      text += ` · Sections: ${r.sections.join(', ')}`;
    }
    if (r.recipientType) {
      text += ` (${r.recipientType})`;
    }
    return text || "No recipients specified";
  };

  // Navigation buttons
  const navButtons = [{
    label: "Salary",
    icon: "fa-wallet",
    color: "linear-gradient(135deg, #0D47A1, #2196F3)",
    path: "/salary"
  }];
  return <div style={{
    minHeight: "100vh",
    background: "#FFFFFF",
    fontFamily: "'Inter', sans-serif"
  }}>
      
      {/* ===== HEADER ===== */}
      <div style={{
      background: "linear-gradient(135deg, #0D47A1, #2196F3)",
      padding: "10px 16px",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      position: "sticky",
      top: 0,
      zIndex: 100,
      boxShadow: "0 4px 20px rgba(13,71,161,0.2)",
      gap: 8
    }}>
        {/* Left: Logo + Title */}
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 10
      }}>
          <div style={{
          width: 34,
          height: 34,
          borderRadius: 12,
          background: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: 14,
          boxShadow: "0 4px 12px rgba(13,71,161,0.3)",
          flexShrink: 0
        }}>
            <i className="fas fa-bullhorn" style={{
            color: "#0D47A1"
          }}></i>
          </div>
          <div>
            <div style={{
            fontWeight: 800,
            fontSize: "clamp(13px, 2vw, 16px)",
            color: "#FFFFFF",
            letterSpacing: "-0.3px",
            whiteSpace: "nowrap"
          }}>
              Admin Circulars
            </div>
            <div style={{
            fontSize: "clamp(7px, 1vw, 9px)",
            color: "#90CAF9",
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            fontWeight: 600
          }}>
              Send Notices & Announcements
            </div>
          </div>
        </div>

        {/* Right: Desktop Buttons + Mobile Menu */}
        <div style={{
        display: "flex",
        alignItems: "center",
        gap: 6
      }}>
          {/* Desktop Buttons */}
          <div className="hide-mobile" style={{
          display: "flex",
          gap: 6,
          alignItems: "center"
        }}>
           
            <button onClick={() => navigate("/admin")} style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 14px",
            borderRadius: 10,
            fontSize: 11,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 6,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              <i className="fas fa-arrow-left"></i> <span>Back</span>
            </button>
          </div>

          {/* Mobile: Menu Toggle */}
          <div className="show-mobile" style={{
          display: "none",
          alignItems: "center",
          gap: 6
        }}>
            <button onClick={() => navigate("/admin")} style={{
            background: "#FFFFFF",
            color: "#0D47A1",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: 4,
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
          }}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} style={{
            background: "rgba(255,255,255,0.15)",
            color: "#FFFFFF",
            padding: "6px 10px",
            borderRadius: 8,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center"
          }}>
              <i className={`fas ${mobileMenuOpen ? "fa-times" : "fa-bars"}`}></i>
            </button>
          </div>
        </div>
      </div>

      {/* ===== MOBILE DROPDOWN MENU ===== */}
      {mobileMenuOpen && <div className="mobile-menu" style={{
      background: "#0D47A1",
      padding: "10px 16px 14px",
      display: "flex",
      flexWrap: "wrap",
      gap: 6,
      position: "sticky",
      top: "54px",
      zIndex: 99,
      boxShadow: "0 4px 16px rgba(13,71,161,0.2)"
    }}>
          {navButtons.map((btn, idx) => <button key={idx} onClick={() => {
        setMobileMenuOpen(false);
        navigate(btn.path);
      }} style={{
        background: "#FFFFFF",
        color: "#0D47A1",
        border: "none",
        padding: "6px 14px",
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "1 0 auto",
        justifyContent: "center",
        minWidth: "80px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
              <i className={btn.icon}></i> {btn.label}
            </button>)}
          <button onClick={() => {
        setMobileMenuOpen(false);
        navigate("/admin");
      }} style={{
        background: "#FFFFFF",
        color: "#0D47A1",
        padding: "6px 14px",
        borderRadius: 8,
        fontSize: 11,
        fontWeight: 600,
        cursor: "pointer",
        display: "flex",
        alignItems: "center",
        gap: 6,
        flex: "1 0 auto",
        justifyContent: "center",
        minWidth: "80px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
      }}>
            <i className="fas fa-arrow-left"></i> Back
          </button>
        </div>}

      {/* ===== MAIN CONTENT ===== */}
      <div style={{
      padding: "16px",
      maxWidth: 1400,
      margin: "0 auto"
    }}>
        <div style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 20,
        // Mobile: Single Column
        "@media (max-width: 768px)": {
          gridTemplateColumns: "1fr"
        }
      }}>
          {/* ===== COMPOSE SECTION ===== */}
          <div style={{
          background: "#FFFFFF",
          borderRadius: 20,
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)"
        }}>
            <div style={{
            padding: "16px 20px",
            background: "#E3F2FD"
          }}>
              <div style={{
              fontWeight: 700,
              fontSize: "clamp(14px, 1.5vw, 16px)",
              color: "#0D47A1"
            }}>
                <i className="fas fa-edit" style={{
                marginRight: 8,
                color: "#0D47A1"
              }}></i> Compose Circular
              </div>
              <div style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              color: "#2196F3",
              marginTop: 2
            }}>
                Create a polished notice for parents & staff
              </div>
            </div>

            <div style={{
            padding: "16px 20px"
          }}>
              {/* Title */}
              <div style={{
              marginBottom: 16
            }}>
                <label style={{
                fontSize: "clamp(10px, 1vw, 11px)",
                color: "#0D47A1",
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                  Circular Title <span style={{
                  color: "#0D47A1"
                }}>*</span>
                </label>
                <input value={title} onChange={e => setTitle(e.target.value)} style={{
                width: "100%",
                background: "#FFFFFF",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: "clamp(12px, 1vw, 13px)",
                color: "#0D47A1",
                outline: "none",
                boxSizing: "border-box",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }} placeholder="Enter circular title..." />
              </div>

              {/* Send To */}
              <div style={{
              marginBottom: 16
            }}>
                <label style={{
                fontSize: "clamp(10px, 1vw, 11px)",
                color: "#0D47A1",
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                  Send To
                </label>
                <div style={{
                display: "flex",
                gap: 10,
                marginBottom: 10,
                flexWrap: "wrap"
              }}>
                  <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "clamp(12px, 1vw, 13px)",
                  color: "#0D47A1",
                  cursor: "pointer"
                }}>
                    <input type="checkbox" checked={allParents} onChange={e => {
                    setAllParents(e.target.checked);
                    if (e.target.checked) setToPrincipal(false);
                  }} style={{
                    accentColor: "#0D47A1",
                    width: 15,
                    height: 15
                  }} />
                    All Parents
                  </label>
                  <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "clamp(12px, 1vw, 13px)",
                  color: "#0D47A1",
                  cursor: "pointer"
                }}>
                    <input type="checkbox" checked={allStaff} onChange={e => {
                    setAllStaff(e.target.checked);
                    if (e.target.checked) setToPrincipal(false);
                  }} style={{
                    accentColor: "#0D47A1",
                    width: 15,
                    height: 15
                  }} />
                    All Staff
                  </label>
                  <label style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: "clamp(12px, 1vw, 13px)",
                  color: "#2196F3",
                  cursor: "pointer",
                  background: toPrincipal ? "#0D47A1" : "transparent",
                  padding: "2px 10px",
                  borderRadius: 6
                }}>
                    <input type="checkbox" checked={toPrincipal} onChange={e => setToPrincipal(e.target.checked)} style={{
                    accentColor: "#2196F3",
                    width: 15,
                    height: 15
                  }} />
                    <span style={{
                    fontWeight: toPrincipal ? 700 : 500,
                    color: toPrincipal ? "#FFFFFF" : "#2196F3"
                  }}> Principal</span>
                  </label>
                </div>

                {!toPrincipal && <>
                    <div style={{
                  fontSize: "clamp(10px, 1vw, 11px)",
                  color: "#2196F3",
                  marginBottom: 6
                }}>
                      Or select classes:
                    </div>
                    <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 6
                }}>
                      {CLASS_OPTIONS.map(c => <button key={c} onClick={() => toggleClass(c)} style={{
                    padding: "3px 12px",
                    borderRadius: 16,
                    fontSize: "clamp(10px, 1vw, 11px)",
                    fontWeight: classesSel.includes(c) ? 700 : 500,
                    background: classesSel.includes(c) ? "#0D47A1" : "#FFFFFF",
                    color: classesSel.includes(c) ? "#FFFFFF" : "#2196F3",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    boxShadow: classesSel.includes(c) ? "0 2px 8px Principal rgba(13,71,161,0.2)" : "0 1px 3px rgba(0,0,0,0.05)"
                  }}>
                          {c}
                        </button>)}
                    </div>

                    {classesSel.length === 1 && <div style={{
                  marginTop: 10,
                  padding: "10px 14px",
                  background: "#E3F2FD",
                  borderRadius: 10
                }}>
                        <div style={{
                    fontSize: "clamp(10px, 1vw, 11px)",
                    color: "#0D47A1",
                    fontWeight: 600
                  }}>
                          <i className="fas fa-info-circle" style={{
                      marginRight: 6
                    }}></i>
                          Clicking "Send Now" will open a popup to select Parents or Staff
                        </div>
                      </div>}
                  </>}

                {toPrincipal && <div style={{
                marginTop: 10,
                padding: "10px 14px",
                background: "#0D47A1",
                borderRadius: 10
              }}>
                    <div style={{
                  fontSize: "clamp(10px, 1vw, 11px)",
                  color: "#FFFFFF",
                  fontWeight: 600
                }}>
                      <i className="fas fa-info-circle" style={{
                    marginRight: 6
                  }}></i>
                      This circular will be sent only to the Principal
                    </div>
                  </div>}
              </div>

              {/* Message */}
              <div style={{
              marginBottom: 16
            }}>
                <label style={{
                fontSize: "clamp(10px, 1vw, 11px)",
                color: "#0D47A1",
                fontWeight: 600,
                display: "block",
                marginBottom: 5,
                textTransform: "uppercase",
                letterSpacing: "0.5px"
              }}>
                  Message (Tamil / English)
                </label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} style={{
                width: "100%",
                background: "#FFFFFF",
                borderRadius: 10,
                padding: "8px 12px",
                fontSize: "clamp(12px, 1vw, 13px)",
                color: "#0D47A1",
                outline: "none",
                boxSizing: "border-box",
                resize: "vertical",
                fontFamily: "'Inter', sans-serif",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }} placeholder="Type your circular here.: Unit Test 2 April 22 to  26 ." />
              </div>

              {/* Buttons */}
              <div style={{
              display: "flex",
              gap: 10,
              flexDirection: "column"
            }}>
                <div style={{
                display: "flex",
                gap: 10
              }}>
                  <button onClick={handleSendNow} disabled={sending} style={{
                  flex: 2,
                  background: sending ? "#2196F3" : toPrincipal ? "linear-gradient(135deg, #2196F3, #0D47A1)" : "linear-gradient(135deg, #0D47A1, #2196F3)",
                  color: "#FFFFFF",
                  border: "none",
                  padding: "10px 16px",
                  borderRadius: 12,
                  fontSize: "clamp(12px, 1vw, 13px)",
                  fontWeight: 700,
                  cursor: sending ? "not-allowed" : "pointer",
                  boxShadow: sending ? "none" : toPrincipal ? "0 4px 14px rgba(13,71,161,0.3)" : "0 4px 14px rgba(13,71,161,0.3)",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6
                }}>
                    <i className="fas fa-paper-plane"></i>
                    {sending ? "Sending..." : "Send Now"}
                  </button>
                  <button onClick={() => {
                  setNotice("Saved as draft");
                }} style={{
                  flex: 1,
                  background: "#E3F2FD",
                  color: "#0D47A1",
                  padding: "10px 16px",
                  borderRadius: 12,
                  fontSize: "clamp(12px, 1vw, 13px)",
                  fontWeight: 600,
                  cursor: "pointer",
                  transition: "all 0.2s",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 6,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                }}>
                    <i className="fas fa-save"></i> Draft
                  </button>
                </div>
              </div>

              {/* Notice */}
              {notice && <div style={{
              marginTop: 12,
              padding: "10px 14px",
              borderRadius: 10,
              background: notice.includes(<XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />) || notice.includes('failed') ? "#E3F2FD" : "#0D47A1",
              color: notice.includes(<XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />) || notice.includes('failed') ? "#0D47A1" : "#FFFFFF",
              fontSize: "clamp(11px, 1vw, 13px)",
              fontWeight: 600,
              display: "flex",
              alignItems: "center",
              gap: 6,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  <span>{notice.includes(<XCircle className="inline w-[1em] h-[1em] align-[-0.125em]" />) || notice.includes('failed') ? "" : <CheckCircle2 className="inline w-[1em] h-[1em] align-[-0.125em]" />}</span> {notice}
                </div>}
            </div>
          </div>

          {/* ===== RECENT CIRCULARS ===== */}
          <div style={{
          background: "#FFFFFF",
          borderRadius: 20,
          boxShadow: "0 2px 12px rgba(13,71,161,0.06)"
        }}>
            <div style={{
            padding: "16px 20px",
            background: "#E3F2FD"
          }}>
              <div style={{
              fontWeight: 700,
              fontSize: "clamp(14px, 1.5vw, 16px)",
              color: "#0D47A1"
            }}>
                <i className="fas fa-history" style={{
                marginRight: 8,
                color: "#0D47A1"
              }}></i> Recent Circulars
              </div>
              <div style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              color: "#2196F3",
              marginTop: 2
            }}>
                Last 50 sent circulars
              </div>
            </div>

            <div style={{
            padding: "12px 16px",
            maxHeight: 600,
            overflowY: "auto"
          }}>
              {recent.length === 0 ? <div style={{
              textAlign: "center",
              padding: 40,
              color: "#2196F3"
            }}>
                  <i className="fas fa-inbox" style={{
                fontSize: 32,
                display: "block",
                marginBottom: 10
              }}></i>
                  <div style={{
                fontSize: "clamp(12px, 1vw, 13px)"
              }}>No circulars sent yet</div>
                </div> : recent.map(r => <div key={r._id} style={{
              borderRadius: 14,
              padding: "12px 14px",
              marginBottom: 10,
              background: "#FFFFFF",
              position: "relative",
              transition: "all 0.2s",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                    <div style={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: 4,
                background: getRecipientAccentColor(r),
                borderRadius: "14px 0 0 14px"
              }} />
                    <div style={{
                paddingLeft: 14
              }}>
                      <div style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: 6,
                  flexWrap: "wrap",
                  gap: 6
                }}>
                        <div style={{
                    fontSize: "clamp(13px, 1.2vw, 14px)",
                    fontWeight: 700,
                    color: "#0D47A1",
                    flex: 1
                  }}>
                          {r.title}
                        </div>
                        <div style={{
                    display: "flex",
                    gap: 4,
                    flexShrink: 0
                  }}>
                          <button onClick={() => startEditCircular(r._id)} style={{
                      background: "#0D47A1",
                      color: "#FFFFFF",
                      border: "none",
                      padding: "3px 8px",
                      borderRadius: 6,
                      fontSize: "clamp(9px, 1vw, 10px)",
                      fontWeight: 600,
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                    }}>
                            <i className="fas fa-edit"></i> <span className="hide-mobile-inline">Edit</span>
                          </button>
                          <button onClick={() => deleteCircular(r._id)} style={{
                      background: "#E3F2FD",
                      color: "#0D47A1",
                      border: "none",
                      padding: "3px 8px",
                      borderRadius: 6,
                      fontSize: "clamp(9px, 1vw, 10px)",
                      fontWeight: 600,
                      cursor: "pointer",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
                    }}>
                            <i className="fas fa-trash"></i>
                          </button>
                        </div>
                      </div>
                      <div style={{
                  fontSize: "clamp(10px, 1vw, 11px)",
                  color: "#2196F3",
                  marginBottom: 4
                }}>
                        {new Date(r.createdAt).toLocaleString()}
                      </div>
                      <div style={{
                  fontSize: "clamp(10px, 1vw, 11px)",
                  color: "#2196F3",
                  marginBottom: 6
                }}>
                        To: {getRecipientText(r)}
                      </div>
                      {r.message && <div style={{
                  fontSize: "clamp(11px, 1vw, 12px)",
                  background: r.recipientsMeta?.toPrincipal ? "#0D47A1" : "#E3F2FD",
                  padding: "8px 12px",
                  borderRadius: 8,
                  color: r.recipientsMeta?.toPrincipal ? "#FFFFFF" : "#0D47A1",
                  marginBottom: 6,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word"
                }}>
                          {r.message.length > 150 ? r.message.slice(0, 150) + '...' : r.message}
                        </div>}
                      <div style={{
                  display: "flex",
                  justifyContent: "flex-end"
                }}>
                        <StatusBadge status={getCircularStatus(r)} />
                      </div>
                    </div>
                  </div>)}
            </div>
          </div>
        </div>
      </div>

      {/* ===== RECIPIENT SELECTION MODAL ===== */}
      {showRecipientModal && <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(13,71,161,0.6)",
      zIndex: 2000,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      backdropFilter: "blur(8px)",
      padding: "16px"
    }} onClick={() => setShowRecipientModal(false)}>
          <div style={{
        background: "#FFFFFF",
        borderRadius: 24,
        width: "min(500px, 100%)",
        maxHeight: "90vh",
        boxShadow: "0 40px 80px rgba(0,0,0,0.1)",
        overflow: "hidden"
      }} onClick={e => e.stopPropagation()}>
            <div style={{
          background: "linear-gradient(135deg, #0D47A1, #2196F3)",
          padding: "16px 20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center"
        }}>
              <div>
                <div style={{
              fontWeight: 800,
              fontSize: "clamp(14px, 1.5vw, 16px)",
              color: "#FFFFFF"
            }}>
                  <i className="fas fa-users" style={{
                marginRight: 6
              }}></i> Send to {selectedClassForModal}
                </div>
                <div style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              color: "#90CAF9",
              marginTop: 2
            }}>
                  Select recipients and sections
                </div>
              </div>
              <button onClick={() => setShowRecipientModal(false)} style={{
            background: "#FFFFFF",
            border: "none",
            color: "#000000",
            borderRadius: 8,
            width: 32,
            height: 32,
            fontSize: 14,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }} aria-label="Cancel and close">
                <X size={18} strokeWidth={2.5} color="#000000" />
              </button>
            </div>

            <div style={{
          padding: "16px 20px"
        }}>
              {/* Recipient Type */}
              <div style={{
            marginBottom: 20
          }}>
                <label style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              color: "#0D47A1",
              fontWeight: 600,
              display: "block",
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
                  Select Recipient Type
                </label>
                <div style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 10
            }}>
                  <button onClick={() => setSelectedType('parents')} style={{
                padding: "12px",
                borderRadius: 12,
                background: selectedType === 'parents' ? '#0D47A1' : '#FFFFFF',
                cursor: "pointer",
                transition: "all 0.2s",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                    <i className="fas fa-users" style={{
                  fontSize: 20,
                  color: selectedType === 'parents' ? '#FFFFFF' : '#2196F3',
                  display: "block",
                  marginBottom: 4
                }}></i>
                    <div style={{
                  fontSize: "clamp(12px, 1vw, 13px)",
                  fontWeight: 700,
                  color: selectedType === 'parents' ? '#FFFFFF' : '#0D47A1'
                }}>Parents</div>
                    <div style={{
                  fontSize: "clamp(9px, 0.8vw, 10px)",
                  color: selectedType === 'parents' ? '#90CAF9' : '#2196F3'
                }}>
                      All parents in selected sections
                    </div>
                  </button>
                  <button onClick={() => setSelectedType('staff')} style={{
                padding: "12px",
                borderRadius: 12,
                background: selectedType === 'staff' ? '#0D47A1' : '#FFFFFF',
                cursor: "pointer",
                transition: "all 0.2s",
                textAlign: "center",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                    <i className="fas fa-user-tie" style={{
                  fontSize: 20,
                  color: selectedType === 'staff' ? '#FFFFFF' : '#2196F3',
                  display: "block",
                  marginBottom: 4
                }}></i>
                    <div style={{
                  fontSize: "clamp(12px, 1vw, 13px)",
                  fontWeight: 700,
                  color: selectedType === 'staff' ? '#FFFFFF' : '#0D47A1'
                }}>Staff</div>
                    <div style={{
                  fontSize: "clamp(9px, 0.8vw, 10px)",
                  color: selectedType === 'staff' ? '#90CAF9' : '#2196F3'
                }}>
                      All staff members
                    </div>
                  </button>
                </div>
              </div>

              {/* Sections */}
              {selectedType && <div style={{
            marginBottom: 20
          }}>
                  <label style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              color: "#0D47A1",
              fontWeight: 600,
              display: "block",
              marginBottom: 6,
              textTransform: "uppercase",
              letterSpacing: "0.5px"
            }}>
                    Select Sections
                  </label>
                  <div style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 6
            }}>
                    {SECTIONS.map(section => <button key={section} onClick={() => toggleSection(section)} style={{
                padding: "4px 14px",
                borderRadius: 16,
                fontSize: "clamp(11px, 1vw, 12px)",
                fontWeight: selectedSections.includes(section) ? 700 : 500,
                background: selectedSections.includes(section) ? '#0D47A1' : '#FFFFFF',
                color: selectedSections.includes(section) ? '#FFFFFF' : '#2196F3',
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: selectedSections.includes(section) ? "0 2px 8px rgba(13,71,161,0.2)" : "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                        Section {section}
                      </button>)}
                    <button onClick={() => setSelectedSections(SECTIONS)} style={{
                padding: "4px 14px",
                borderRadius: 16,
                fontSize: "clamp(11px, 1vw, 12px)",
                fontWeight: selectedSections.length === SECTIONS.length ? 700 : 500,
                background: selectedSections.length === SECTIONS.length ? '#0D47A1' : '#FFFFFF',
                color: selectedSections.length === SECTIONS.length ? '#FFFFFF' : '#2196F3',
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow: selectedSections.length === SECTIONS.length ? "0 2px 8px rgba(13,71,161,0.2)" : "0 1px 3px rgba(0,0,0,0.05)"
              }}>
                      All Sections
                    </button>
                  </div>
                </div>}

              {/* Summary */}
              {selectedType && selectedSections.length > 0 && <div style={{
            padding: "12px 14px",
            background: "#E3F2FD",
            borderRadius: 12,
            marginBottom: 20
          }}>
                  <div style={{
              fontSize: "clamp(10px, 1vw, 11px)",
              fontWeight: 600,
              color: "#0D47A1",
              marginBottom: 6
            }}>
                    Summary
                  </div>
                  <div style={{
              display: "grid",
              gap: 3
            }}>
                    <div style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "clamp(11px, 1vw, 12px)"
              }}>
                      <span style={{
                  color: "#2196F3"
                }}>Recipient Type:</span>
                      <span style={{
                  fontWeight: 600,
                  color: "#0D47A1",
                  textTransform: "capitalize"
                }}>
                        {selectedType}
                      </span>
                    </div>
                    <div style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "clamp(11px, 1vw, 12px)"
              }}>
                      <span style={{
                  color: "#2196F3"
                }}>Class:</span>
                      <span style={{
                  fontWeight: 600,
                  color: "#0D47A1"
                }}>
                        {selectedClassForModal}
                      </span>
                    </div>
                    <div style={{
                display: "flex",
                justifyContent: "space-between",
                fontSize: "clamp(11px, 1vw, 12px)"
              }}>
                      <span style={{
                  color: "#2196F3"
                }}>Sections:</span>
                      <span style={{
                  fontWeight: 600,
                  color: "#0D47A1"
                }}>
                        {selectedSections.join(', ')}
                      </span>
                    </div>
                  </div>
                </div>}

              {/* Buttons */}
              <div style={{
            display: "flex",
            gap: 10
          }}>
                <button onClick={() => setShowRecipientModal(false)} style={{
              flex: 1,
              background: "#E3F2FD",
              color: "#0D47A1",
              border: "none",
              padding: "10px",
              borderRadius: 12,
              fontSize: "clamp(11px, 1vw, 12px)",
              fontWeight: 600,
              cursor: "pointer",
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)"
            }}>
                  Cancel
                </button>
                <button onClick={handleModalSend} disabled={!selectedType || selectedSections.length === 0 || modalLoading} style={{
              flex: 2,
              background: !selectedType || selectedSections.length === 0 || modalLoading ? "#2196F3" : "linear-gradient(135deg, #0D47A1, #2196F3)",
              color: "#FFFFFF",
              border: "none",
              padding: "10px",
              borderRadius: 12,
              fontSize: "clamp(11px, 1vw, 12px)",
              fontWeight: 700,
              cursor: !selectedType || selectedSections.length === 0 || modalLoading ? "not-allowed" : "pointer",
              boxShadow: !selectedType || selectedSections.length === 0 || modalLoading ? "none" : "0 4px 14px rgba(13,71,161,0.3)",
              transition: "all 0.2s"
            }}>
                  {modalLoading ? <span>
                      <i className="fas fa-spinner fa-pulse" style={{
                  marginRight: 6
                }}></i>
                      Sending...
                    </span> : "Send Now"}
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* ===== CSS for Mobile ===== */}
      <style>{`
        @media (max-width: 768px) {
          .hide-mobile { display: none !important; }
          .show-mobile { display: flex !important; }
          .hide-mobile-inline { display: none !important; }
          
          /* Main layout: stack vertically */
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
          
          /* Card padding adjustments */
          div[style*="padding: 24px"] {
            padding: 16px !important;
          }
        }
        
        @media (min-width: 769px) {
          .show-mobile { display: none !important; }
          .hide-mobile-inline { display: inline !important; }
        }
      `}</style>
    </div>;
}